import { createModelSegment } from 'client/data/luckdragon/segment';
import { constant, flow, get, set } from 'lodash-es';
import sha1 from 'hash.js/lib/hash/sha/1';
import { EdmundsAPI, json } from 'client/data/api/api-client';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { VisitorModel } from 'client/data/models/visitor';
import { HTTP_ACCEPTED, HTTP_NOT_FOUND } from 'client/utils/http-status';
import {
  DMA,
  LEASE_PAYMENT,
  LOAN_PAYMENT,
  ZIP_CODE,
} from 'site-modules/shared/constants/allowed-inventory-request-params';
import { withRetry } from 'client/utils/promise';
import { withMetrics } from 'client/data/api/api-metrics';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { gql } from '@apollo/client';
import { getCBPSortSRPFilter } from 'site-modules/shared/utils/car-buying/srp-filter';
import { filterAvailableApiKeys, prepareQueryParamsMap } from 'site-modules/shared/utils/inventory/params-conversion';
import { addDaysToCurrentDate } from 'site-modules/shared/utils/date-utils';
import { PersonalizedSearchStorage } from 'site-modules/shared/utils/personalized-search/storage';
import { abortControllerManager } from 'site-modules/shared/utils/personalized-search/abort-controller-manager';
import { getTargetingDataFromInventory } from 'site-modules/shared/components/native-ad/utils/get-ad-targeting';
import { omitEmptyComponents } from 'site-modules/shared/utils/personalized-search/omit-empty-components';
import {
  isAveragePricePathExists,
  isEditorialRatingPathExists,
  isRecommendedTrimPathExists,
} from 'site-modules/personalized-search/utils/check-paths-existence';
import { logger } from 'client/utils/logger/logger';
import { PageModel } from 'client/data/models/page';
import { SEARCH_KEY_QUERY_PARAM } from 'site-modules/shared/constants/personalized-search/personalized-search';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { getRadiusByDmaRank } from 'site-modules/shared/utils/get-radius-by-dma-rank';
import { API_LINKS_TYPE } from 'site-modules/shared/constants/global-search/global-search';

export const LLM_TIMEOUT = 10000;
export const LLM_GET_RETRIES_COUNT = 2;

const withRetry202Response = (promiseCall, retries, delayMS) => {
  // Rejects promise when 202 response received to let withRetry helper trigger retry.
  const reject202Response = response => {
    if (response.status === HTTP_ACCEPTED) {
      return Promise.reject({});
    }

    return response;
  };

  return withRetry((...params) => promiseCall(...params).then(reject202Response), retries, delayMS);
};

async function getSearchId({ userSearch, zipCode, abortController, history, url }) {
  const request = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      question: userSearch,
      extra: { facets: { zip: zipCode } },
      ...(history ? { history } : {}),
    }),
    timeout: LLM_TIMEOUT,
  };

  return EdmundsAPI.fetchJson(url, {
    ...request,
    signal: abortController.signal,
  });
}

async function getSearchData({ searchId, abortController }) {
  const fetchWithoutBuiltinRetries = async (resourcePath, options) =>
    // We pass retries: 0 here for disabling default retry logic, provided by api-client, because of explicit usage of withRetry below
    EdmundsAPI.fetch(resourcePath, { ...options, retries: 0 });

  const fetchWithCustomRetry = withRetry202Response(fetchWithoutBuiltinRetries, LLM_GET_RETRIES_COUNT);
  const searchDataResponse = await fetchWithCustomRetry(`/llm/v1/get/${searchId}/?meta=true&input=true`, {
    timeout: LLM_TIMEOUT,
    signal: abortController.signal,
  });

  return json(searchDataResponse);
}

const filterOutUnsupportedDealershipComponents = ({ struct, ...rest }) => {
  return {
    ...rest,
    struct: {
      ...struct,
      dealership: struct?.dealership?.filter(({ type }) => type === API_LINKS_TYPE.DEALERSHIP_PROFILE),
    },
  };
};

const limitDealershipComponents = ({ struct, ...rest }) => {
  return {
    ...rest,
    struct: {
      ...struct,
      dealership: struct?.dealership?.slice(0, 3),
    },
  };
};

const skipEmptyComponents = data => ({
  ...data,
  struct: omitEmptyComponents(data.struct),
});

const transformStruct = (data, key, transformFn) => {
  const targetArray = data?.struct?.[key];

  return {
    ...data,
    struct: {
      ...data.struct,
      ...(targetArray && Array.isArray(targetArray) ? { [key]: targetArray.map(transformFn) } : {}),
    },
  };
};

const wrapItemInComponent = itemName => item => ({
  components: [itemName],
  [itemName]: { ...item },
});

export const appendOverviewLearnMore = data =>
  transformStruct(data, 'overview', overviewItem => ({
    ...overviewItem,
    components: [...(overviewItem.components || []), 'learnMore'],
    learnMore: { stub: true },
  }));

const transformOverviewItems = data => transformStruct(data, 'overview', wrapItemInComponent('overviewItem'));

const transformLinkItems = data => transformStruct(data, 'links', wrapItemInComponent('linksItem'));

const transformAnchorItems = data => transformStruct(data, 'anchor', wrapItemInComponent('anchorItem'));

const transformDealershipItems = data => transformStruct(data, 'dealership', wrapItemInComponent('dealershipItem'));

const getSearchAborted = () => 'searchAborted';
const getSearchError = () => 'error';
const getLlmSearchSubmitParams = () => `llmSearchSubmitParams`;
const getSupposedPageStruct = key => `supposedPageStruct.${key}`;
const getPageStruct = key => `pageStruct.${key}`;
const getSelectedFilters = () => 'selectedFilters';
const getInventoryData = () => 'inventoryData';
const getInventoryPriceIntervals = () => 'inventoryPriceIntervals';
const getInventoryPriceIntervalsQuery = () => 'inventoryPriceIntervalsQuery';
const getAdTargeting = () => 'adTargeting';
const getEditorialRatingPath = ({ make, model, submodel, year }) =>
  isEditorialRatingPathExists({ make, model, submodel, year })
    ? `makes["${make}"].models["${model}"].submodels["${submodel}"].years["${year}"].editorialRating`
    : null;
const getEditorialTrims = ({ make, model, year }) =>
  isRecommendedTrimPathExists({ make, model, year })
    ? `makes["${make}"].models["${model}"].years["${year}"].editorialTrims`
    : null;
const getRecommendedTrimPath = ({ make, model, year }) =>
  isRecommendedTrimPathExists({ make, model, year })
    ? `makes["${make}"].models["${model}"].years["${year}"].recommendedTrim`
    : null;
const getAveragePricePath = ({ make, model, year }) =>
  isAveragePricePathExists({ make, model, year })
    ? `makes["${make}"].models["${model}"].years["${year}"].averagePrice`
    : null;

export const PersonalizedSearchPaths = {
  getSelectedFilters,
  getInventoryData,
  getEditorialRatingPath,
  getInventoryPriceIntervals,
  getInventoryPriceIntervalsQuery,
  getAdTargeting,
  getEditorialTrims,
  getRecommendedTrimPath,
  getSearchError,
  getSearchAborted,
  getLlmSearchSubmitParams,
  getSupposedPageStruct,
  getPageStruct,
  getAveragePricePath,
};

export const ERROR_STATUSES = {
  EXPIRED: 'expired',
  UNKNOWN: 'unknown',
};

const PREV_SEARCH_KEY = 'prevPersonalizedSearch';

const withSearchHistory = fetcher => async args => {
  const previousSearch = PersonalizedSearchStorage.getValueGivenExpirationDate(PREV_SEARCH_KEY);

  const response = await fetcher({ ...args, history: previousSearch });

  if (response?.thread?.uuid) {
    PersonalizedSearchStorage.setValueToLocalStorage(
      PREV_SEARCH_KEY,
      JSON.stringify({ uuid: response.thread.uuid, expirationDate: addDaysToCurrentDate(1) })
    );
  }

  return response;
};

const getExpiredErrorStatus = e => (e.status === HTTP_NOT_FOUND ? ERROR_STATUSES.EXPIRED : ERROR_STATUSES.UNKNOWN);

const isAborted = e => e.name === 'AbortError';

const persistError =
  (fetcher, context, getErrorStatus = constant(ERROR_STATUSES.UNKNOWN)) =>
  async args => {
    try {
      await context.updateValue('error', undefined);

      return await fetcher(args);
    } catch (e) {
      await context.updateValue('error', getErrorStatus(e));
      throw e;
    }
  };

const withAbort = (fetcher, context) => async args => {
  const abortController = new AbortController();
  abortControllerManager.register(abortController);

  try {
    await context.updateValue(getSearchAborted(), false);

    return await fetcher({ ...args, abortController });
  } finally {
    abortControllerManager.unregister(abortController);
  }
};

export const PersonalizedSearchModel = createModelSegment('personalizedSearch', [
  {
    /**
     * @see getSearchAborted
     */
    path: 'searchAborted',
    async update(newValue) {
      if (newValue) {
        abortControllerManager.abort();
      }
      return newValue;
    },
  },
  {
    /**
     * @see getSearchError
     */
    path: 'error',
  },
  {
    /**
     * @see getLlmSearchSubmitParams
     */
    path: 'llmSearchSubmitParams',
    async resolve(match, context) {
      const queryParams = await context.resolveValue('location.query', PageModel, false);

      const key = queryParams?.[SEARCH_KEY_QUERY_PARAM];

      // Cache key is present in the url. We can take already generated data without resubmission
      if (key) {
        return {
          id: key,
        };
      }

      return {};
    },
    async update(query, match, context) {
      if (!query) {
        return {};
      }

      const { zipCode } = await context.resolveValue('location', VisitorModel, false);

      const url = '/pov/fallback-source-0/?meta=true&cache=false&store_history=true';

      try {
        const fetcher = persistError(withAbort(withSearchHistory(getSearchId), context), context);

        return await fetcher({
          userSearch: decodeURIComponent(query),
          zipCode,
          url: `/llm/v1/ask${url}`,
        });
      } catch (e) {
        if (isAborted(e)) {
          return context.abort();
        }
        logger.error(e);
        return null;
      }
    },
  },
  {
    /**
     * @see getSupposedPageStruct
     */
    path: 'supposedPageStruct.{key}',
    async resolve(match, context) {
      const { key } = match;

      try {
        const fetcher = withAbort(getSearchData, context);

        return await fetcher({ searchId: key });
      } catch (e) {
        if (isAborted(e)) {
          return context.abort();
        }
        logger.error(e);
        return null;
      }
    },
  },
  {
    /**
     * @see getPageStruct
     */
    path: 'pageStruct.{key}',
    async resolve(match, context) {
      const { key } = match;

      const transformSearchData = flow(
        skipEmptyComponents,
        filterOutUnsupportedDealershipComponents,
        limitDealershipComponents,
        transformDealershipItems,
        appendOverviewLearnMore,
        transformOverviewItems,
        transformLinkItems,
        transformAnchorItems
      );

      try {
        const fetcher = persistError(withAbort(getSearchData, context), context, getExpiredErrorStatus);
        const pageStruct = await fetcher({ searchId: key });

        return transformSearchData(pageStruct);
      } catch (e) {
        if (isAborted(e)) {
          return context.abort();
        }
        logger.error(e);
        return null;
      }
    },
  },
  /**
   * @see getEditorialRatingPath
   * https://qa-21-www.edmunds.com/internal/editorial/graphiql/?query=%7B%0A%20%20editorialSubmodels(make%3A%22honda%22%2Cmodel%3A%22accord%22%2Cyear%3A%222024%22)%20%7B%0A%20%20%20%20submodel%0A%20%20%20%20rating_number%0A%20%20%20%20rating_enum%0A%20%20%7D%0A%7D
   */
  {
    path: 'makes["{make}"].models["{model}"].submodels["{submodel}"].years["{year}"].editorialRating',
    async resolve(match, context) {
      const response = await withMetrics(EdmundsGraphQLFederation, context).query(
        gql`
          query ($make: String!, $model: String!, $submodel: String!, $year: String!) {
            editorialSubmodels(make: $make, model: $model, submodel: $submodel, year: $year) {
              submodel
              rating_number
              rating_enum
            }
          }
        `,
        {
          make: match.make,
          model: match.model,
          submodel: match.submodel,
          year: match.year,
        }
      );

      return get(response, `editorialSubmodels[0]`, null);
    },
  },

  // Inventory
  /**
   * @see getSelectedFilters
   */
  {
    path: 'selectedFilters',
  },

  /**
   * @see getInventoryData
   */
  {
    path: 'inventoryData',
    async resolve(match, context) {
      let filters = await context.resolveValue(getSelectedFilters());

      if (!filters) {
        return null;
      }

      const rooftopId = filters?.rooftopId?.[0];
      let apiPath;
      const options = { timeout: 1000 };

      if (rooftopId) {
        apiPath = `/purchasefunnel/v1/srp/rooftop/${rooftopId}`;
      } else {
        apiPath = '/purchasefunnel/v1/srp/inventory';
        // skipCBPButton feature flag for SRP sorting --- start
        const skipCBPButton = await context.resolveValue('skipCBPButton', FeatureFlagModel);
        filters = getCBPSortSRPFilter({ filters, skipCBPButton });
      }

      const { zipCode, dma, latitude, longitude, stateCode } = await context.resolveValue(
        'location',
        VisitorModel,
        false // we need prevent update location here, because we do update manually from the page component
      );

      const addLatLongParams = !!latitude && !!longitude;
      const latLongQueryParams = addLatLongParams ? { lat: latitude, lon: longitude } : {};
      const defaultRadiusParam = dma ? { [DMA]: dma } : {};

      const locationQueryParams = { [ZIP_CODE]: zipCode, ...defaultRadiusParam, ...latLongQueryParams };
      const pageSize = 21;

      const queryObj = flow(
        filterAvailableApiKeys,
        prepareQueryParamsMap
      )({
        ...locationQueryParams,
        ...filters,
        national: false,
        pageSize,
        stateCode,
      });

      const formattedUrl = objectToQueryString(queryObj);

      const response = await withMetrics(EdmundsAPI, context).fetchJson(
        `${apiPath}?${formattedUrl}&fetchSuggestedFacets=false`,
        options
      );
      const facets = get(response, 'facets', []).map(facet => {
        const { upperBound, values, type } = facet;
        if (upperBound === false && (type === LOAN_PAYMENT || type === LEASE_PAYMENT)) {
          const index = values.length - 1;
          return { ...facet, showAboveUpperBoundValue: true, values: [...values, { name: `${values[index].name}+` }] };
        }
        return facet;
      });

      set(response, 'facets', facets);

      return {
        ...response,
        hash: sha1().update(formattedUrl).digest('hex'),
      };
    },
  },

  /**
   * Gets query params from purchase funnel attributes for aggregate API
   * @see getInventoryPriceIntervalsQuery
   */
  {
    path: 'inventoryPriceIntervalsQuery',
    async resolve(match, context) {
      const searchResults = await context.resolveValue(getInventoryData(), PersonalizedSearchModel, false);

      return searchResults?.attributes?.queryForByIntervalAggregation;
    },
  },

  /**
   * Get price intervals of existing search results
   * @see getInventoryPriceIntervals
   */
  {
    path: 'inventoryPriceIntervals',
    async resolve(match, context) {
      const query = await context.resolveValue(getInventoryPriceIntervalsQuery());

      if (!query) {
        return [];
      }

      const response = await withMetrics(EdmundsAPI, context).fetchJson(`/inventory/v5/aggregate?${query}`, context);

      return response?.buckets;
    },
  },

  /**
   * @see getAdTargeting
   */
  {
    path: 'adTargeting',
    async resolve(_, context) {
      const [inventoryData, selectedFilters] = await Promise.all([
        context.resolveValue(getInventoryData()),
        context.resolveValue(getSelectedFilters(), PersonalizedSearchModel, false),
      ]);

      return getTargetingDataFromInventory({ inventoryData, selectedFilters });
    },
  },
  /**
   * @see getEditorialTrims
   */
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].editorialTrims',
    async resolve(match, context) {
      const response = await withMetrics(EdmundsGraphQLFederation, context).query(
        gql`
          query ($make: String!, $model: String!, $year: String!) {
            editorialTrims(make: $make, model: $model, year: $year) {
              trim
              recommended
              displayName
              labels {
                label
                labelDisplay
                labelText
                authors {
                  name
                  authorTitle
                  image
                  bio
                }
                type
                entryId
              }
            }
          }
        `,
        {
          make: match.make,
          model: match.model,
          year: match.year,
        }
      );

      return get(response, `editorialTrims`, []);
    },
  },

  /**
   * @see getRecommendedTrimPath
   * https://qa-21-www.edmunds.com/internal/editorial/graphiql/?query=%7B%0A%09editorialTrims(make%3A%22bmw%22%2C%20model%3A%22x7%22%2C%20year%3A%222025%22)%20%7B%0A%20%20%20%20trim%0A%20%20%20%20recommended%0A%20%20%7D%20%0A%7D
   */
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].recommendedTrim',
    async resolve(match, context) {
      const { make, model, year } = match;
      const editorialTrims = await context.resolveValue(
        getEditorialTrims({ make, model, year }),
        PersonalizedSearchModel
      );

      return editorialTrims.find(trim => trim.recommended);
    },
  },
  /**
   * @see getAveragePricePath
   * https://qa-11-www.edmunds.com/api/inventory/v5/searchresultspage?-dealerInfo.dealerRelation=RESTRICTED&-dealerInfo.displayInfo.parentDealershipName=Edmunds&-dealerInfo.partnerCode=PRIVATE_PARTY_EDMUNDS&deliveryType=all&lat=34.01663&lon=-118.495341&pagenum=1&pagesize=1&radius=25.0&searchable=true&strategy=website&userDma=803&userState=CA&vehicleInfo.styleInfo.make.nicename=ford&vehicleInfo.styleInfo.year=2024-2024&view=DEFAULT_SRP&zip=90401&vehicleInfo.styleInfo.model.nicename=escape
   */
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].averagePrice',
    async resolve(match, context) {
      const { make, model, year } = match;
      const { zipCode, dma, dmaRank, latitude, longitude, stateCode } = await context.resolveValue(
        'location',
        VisitorModel
      );
      const params = objectToQueryString({
        '-dealerInfo.dealerRelation': 'RESTRICTED',
        '-dealerInfo.displayInfo.parentDealershipName': 'Edmunds',
        '-dealerInfo.partnerCode': 'PRIVATE_PARTY_EDMUNDS',
        deliveryType: 'all',
        lat: latitude,
        lon: longitude,
        pagenum: 1,
        pagesize: 1,
        radius: getRadiusByDmaRank(dmaRank),
        searchable: true,
        strategy: 'website',
        userDma: dma,
        userState: stateCode,
        'vehicleInfo.styleInfo.make.nicename': make,
        'vehicleInfo.styleInfo.year': year,
        view: 'DEFAULT_SRP',
        zip: zipCode,
        'vehicleInfo.styleInfo.model.nicename': model,
      });
      const response = await withMetrics(EdmundsAPI, context).fetchJson(
        `/inventory/v5/searchresultspage?${params}`,
        context
      );

      return response?.aggregations?.stats?.['avg_prices.displayPrice'];
    },
  },
]);
